import { Application, Container, Graphics, Rectangle, Text } from "pixi.js";
import { pieceColour } from "../core/pieceColours";
import { For, Show, createEffect, createSignal, onCleanup, onMount, type JSX } from "solid-js";
import { measureBetween, sampleSegment, type DraftBlock, type DraftResult, type PointTuple, type TapeMeasurement } from "@tailor-frame/engine";
import { packBoxes } from "../core/pieceLayout";

interface Props {
  draft: DraftResult;
  showGrid: boolean;
  showPoints: boolean;
  showCurveHandles: boolean;
  showMannequin: boolean;
  selectedId?: string;
  onSelect: (selection?: WorkspaceSelection) => void;
  /** Pieces left out of the print; each frame's printer button toggles its piece. */
  printExcluded: string[];
  onTogglePrint: (blockId: string) => void;
  /** Extra controls (display toggles) shown in the canvas toolbar. */
  tools?: JSX.Element;
  /** Design choices shown in a bar above the toolbar. */
  design?: JSX.Element;
}

export interface WorkspaceSelection {
  id: string;
  kind: "point" | "edge" | "control-handle";
  position?: [number, number];
}

export function CadWorkspace(props: Props) {
  let host!: HTMLDivElement;
  let app: Application | undefined;
  let camera: Container | undefined;
  let tapeLayer: Container | undefined;
  let pointTooltips: Container[] = [];
  let scale = 0.9;
  let dragging = false;
  let measuring = false;
  let previous = { x: 0, y: 0 };
  // Touch: every finger on the canvas, so two of them pinch-zoom about their midpoint.
  const touches = new Map<number, { x: number; y: number }>();
  let pinch: { distance: number; midpoint: { x: number; y: number } } | undefined;
  const coarsePointer = typeof matchMedia === "function" && matchMedia("(pointer: coarse)").matches;
  let tapeStart: PointTuple | undefined;
  let tapeEnd: PointTuple | undefined;
  const [tapeActive, setTapeActive] = createSignal(false);
  const [tapeReading, setTapeReading] = createSignal<TapeMeasurement>();
  // Each piece sits in its own frame; frames are packed to suit the canvas shape.
  let frames = new Map<string, Rectangle>();
  let placements = new Map<string, PointTuple>();
  let layoutSize = { width: 0, height: 0 };
  let packedAspect = 0;
  let lastTap: { blockId: string; time: number } | undefined;

  const render = () => {
    if (!app || !camera) return;
    camera.removeChildren().forEach((child) => child.destroy());
    pointTooltips = [];

    const aspect = app.screen.width / Math.max(app.screen.height, 1);
    packedAspect = aspect;
    const layout = layoutPieces(props.draft, aspect);
    frames = layout.frames;
    placements = layout.placements;
    layoutSize = { width: layout.width, height: layout.height };

    const colourOf = (blockId: string | undefined) => pieceColour(Math.max(0, props.draft.blocks.findIndex((candidate) => candidate.id === blockId)));
    for (const block of props.draft.blocks) {
      const colour = colourOf(block.id);
      const panel = new Container();
      const placement = placements.get(block.id) ?? block.offset;
      const frame = frames.get(block.id) ?? new Rectangle(block.bounds.minX, block.bounds.minY, block.bounds.width, block.bounds.height);
      panel.position.set(placement[0], placement[1]);
      const printed = !props.printExcluded.includes(block.id);
      const background = new Graphics()
        .roundRect(frame.x, frame.y, frame.width, frame.height, 6)
        .fill({ color: 0x141d21 });
      panel.addChild(background);
      if (props.showGrid) panel.addChild(frameGrid(frame));
      const border = new Graphics()
        .roundRect(frame.x, frame.y, frame.width, frame.height, 6)
        .stroke({ color: colour, width: 1.2 });
      border.alpha = 0.45;
      const header = new Graphics()
        .moveTo(frame.x, frame.y + FRAME_HEADER)
        .lineTo(frame.x + frame.width, frame.y + FRAME_HEADER)
        .stroke({ color: colour, width: 0.8, alpha: 0.35 });
      panel.addChild(border, header);
      panel.eventMode = "static";
      panel.hitArea = frame;
      panel.on("pointerenter", () => { border.alpha = 1; });
      panel.on("pointerleave", () => { border.alpha = 0.45; });
      // Double-click a piece's frame to zoom to just that piece.
      panel.on("pointertap", () => {
        const now = performance.now();
        if (!tapeActive() && lastTap?.blockId === block.id && now - lastTap.time < 350) {
          focusPiece(block.id);
          lastTap = undefined;
        } else lastTap = { blockId: block.id, time: now };
      });
      const label = new Text({ text: block.label, style: { fill: colour, fontSize: 16, fontFamily: "monospace", fontWeight: "bold" } });
      label.position.set(frame.x + 14 + PRINT_BUTTON + 8, frame.y + (FRAME_HEADER - label.height) / 2);
      const summary = new Text({ text: `${formatDimension(block.bounds.width)} × ${formatDimension(block.bounds.height)} · cut ${block.cutQuantity}`, style: { fill: 0x8ea1aa, fontSize: 12, fontFamily: "monospace" } });
      summary.anchor.set(1, 0);
      summary.position.set(frame.x + frame.width - 14, frame.y + (FRAME_HEADER - summary.height) / 2);
      const printToggle = printButton(printed);
      printToggle.position.set(frame.x + 12, frame.y + (FRAME_HEADER - PRINT_BUTTON) / 2);
      printToggle.on("pointerdown", (event) => event.stopPropagation());
      printToggle.on("pointertap", (event) => {
        event.stopPropagation();
        props.onTogglePrint(block.id);
      });
      if (!printed || summary.width + label.width + PRINT_BUTTON + 50 > frame.width) summary.visible = false;
      panel.addChild(label, summary, printToggle);

      const centreX = block.bounds.minX + block.bounds.width / 2;
      const centreY = block.bounds.minY + block.bounds.height / 2;
      const grainLength = Math.min(90, Math.max(45, block.bounds.height * 0.22));
      const grain = new Graphics();
      if (block.grainDirection === "horizontal") {
        drawGrainArrow(grain, [centreX - grainLength / 2, centreY + 42], [centreX + grainLength / 2, centreY + 42]);
      } else if (block.grainDirection === "bias") {
        drawGrainArrow(grain, [centreX - grainLength * 0.35, centreY + 42 + grainLength * 0.35], [centreX + grainLength * 0.35, centreY + 42 - grainLength * 0.35]);
      } else {
        drawGrainArrow(grain, [centreX, centreY + 35 + grainLength / 2], [centreX, centreY + 35 - grainLength / 2]);
      }
      grain.stroke({ color: 0xd7e3e8, width: 1.2, alpha: 0.9 });
      panel.addChild(grain);

      const pieceLabel = new Text({
        text: `${block.label}\n${formatDimension(block.bounds.width)} W × ${formatDimension(block.bounds.height)} H\nCut ${block.cutQuantity} · Grain: ${block.grainDirection}\nMade for: ${props.draft.madeFor}\nPrinted: ${props.draft.printedDate} · Pattern v${props.draft.patternVersion}`,
        style: { fill: 0xe3edf1, fontSize: 11, fontFamily: "monospace", align: "center", lineHeight: 15 },
      });
      pieceLabel.anchor.set(0.5);
      pieceLabel.position.set(centreX, centreY - 25);
      panel.addChild(pieceLabel);

      for (const allowance of props.draft.seamAllowances.filter((item) => block.seamAllowanceIds.includes(item.id))) {
        const hatch = new Graphics();
        const flat = allowance.polygon.flatMap(([x, y]) => [x, y]);
        hatch.poly(flat, true).fill({ color: 0x6b8f71, alpha: 0.16 });
        drawCrossHatch(hatch, allowance.polygon, 8);
        hatch.stroke({ color: 0x91b497, width: 0.65, alpha: 0.65 });
        panel.addChild(hatch);

        // Keep the cut perimeter visually independent from the hatch strokes.
        const cutLine = new Graphics()
          .poly(flat, true)
          .stroke({ color: 0xb9d9bd, width: 1.5, alpha: 1 });
        panel.addChild(cutLine);
      }

      // Inlay beyond the seam allowance: copper, hatched the other way, with
      // its own outer cut line so it reads apart from the allowance.
      for (const inlay of props.draft.inlays.filter((item) => block.inlayIds.includes(item.id))) {
        const flat = inlay.polygon.flatMap(([x, y]) => [x, y]);
        const hatch = new Graphics();
        hatch.poly(flat, true).fill({ color: 0xd08c4f, alpha: 0.16 });
        drawCrossHatch(hatch, inlay.polygon, 6, -1);
        hatch.stroke({ color: 0xd9a066, width: 0.65, alpha: 0.7 });
        panel.addChild(hatch);
        panel.addChild(new Graphics().poly(flat, true).stroke({ color: 0xe8b07a, width: 1.5, alpha: 1 }));
        const label = new Text({ text: `inlay ${formatDimension(inlay.distance)}`, style: { fill: 0xf2c79a, fontSize: 9, fontFamily: "monospace" } });
        label.anchor.set(0.5);
        label.position.set(inlay.labelAt[0], inlay.labelAt[1]);
        panel.addChild(label);
      }

      // Net body overlay at 1:1 on the piece: the gap to the cut line is the ease.
      if (props.showMannequin && block.bodyOverlay) {
        const outline = block.bodyOverlay.edges.flatMap((edge) => edge.segments.flatMap((segment) => sampleSegment(segment, segment.kind === "bezier" ? 24 : 1)));
        const body = new Graphics()
          .poly(outline.flatMap(([x, y]) => [x, y]), true)
          .fill({ color: 0x78909c, alpha: 0.22 })
          .stroke({ color: 0xb8c8d0, width: 1.2, alpha: 0.8 });
        panel.addChild(body);
        const anchor = block.bodyOverlay.edges[1]?.segments[0]?.start;
        if (block.bodyOverlay.label && anchor) {
          const label = new Text({ text: block.bodyOverlay.label, style: { fill: 0xb8c8d0, fontSize: 11, fontFamily: "monospace" } });
          label.position.set(anchor[0] + 6, anchor[1] + 6);
          panel.addChild(label);
        }
      }

      // Reference overlays: dashed, display-only guides (e.g. the yoke joined at the shoulder).
      for (const overlay of block.referenceOverlays) {
        const guide = new Graphics();
        for (const edge of overlay.edges) {
          for (const segment of edge.segments) {
            const samples = sampleSegment(segment, segment.kind === "bezier" ? 32 : 1);
            for (let index = 1; index < samples.length; index += 1) drawDashedLine(guide, samples[index - 1], samples[index], 6, 4);
          }
        }
        guide.stroke({ color: colourOf(overlay.sourcePieceId), width: 1.6, alpha: 0.9 });
        panel.addChild(guide);
        const start = overlay.edges[0]?.segments[0]?.start;
        if (overlay.label && start) {
          const label = new Text({ text: overlay.label, style: { fill: colourOf(overlay.sourcePieceId), fontSize: 11, fontFamily: "monospace" } });
          label.position.set(start[0] + 8, start[1] - 18);
          panel.addChild(label);
        }
      }

      // Internal construction lines (fly, pocket shapes): dashed, inside the piece.
      for (const edge of props.draft.edges.filter((item) => block.internalEdgeIds.includes(item.id))) {
        const guide = new Graphics();
        for (const segment of edge.segments) {
          const samples = sampleSegment(segment, segment.kind === "bezier" ? 32 : 1);
          for (let index = 1; index < samples.length; index += 1) drawDashedLine(guide, samples[index - 1], samples[index], 5, 4);
        }
        guide.stroke({ color: 0xe9c46a, width: 1.4, alpha: 0.85 });
        panel.addChild(guide);
      }

      for (const edge of props.draft.edges.filter((item) => block.edgeIds.includes(item.id))) {
        const from = props.draft.points[edge.from];
        const to = props.draft.points[edge.to];
        if (!from || !to) continue;
        const graphic = new Graphics();
        for (const segment of edge.segments) {
          graphic.moveTo(segment.start[0], segment.start[1]);
          if (segment.kind === "bezier") graphic.bezierCurveTo(segment.control1[0], segment.control1[1], segment.control2[0], segment.control2[1], segment.end[0], segment.end[1]);
          else graphic.lineTo(segment.end[0], segment.end[1]);
        }
        graphic.stroke({ color: 0xe9c46a, width: 2.2 });
        graphic.eventMode = "static";
        graphic.cursor = "pointer";
        graphic.hitArea = { contains: (x: number, y: number) => edge.segments.some((segment) => {
          const samples = sampleSegment(segment, 24);
          return samples.slice(1).some((point, index) => distanceToSegment(x, y, samples[index], point) < 8);
        }) };
        graphic.on("pointertap", (event) => {
          event.stopPropagation();
          if (!tapeActive()) props.onSelect({ id: edge.id, kind: "edge" });
        });
        panel.addChild(graphic);

        if (props.showCurveHandles) {
          edge.segments.forEach((segment, segmentIndex) => {
            if (segment.kind !== "bezier") return;
            const controls = new Graphics()
              .moveTo(segment.start[0], segment.start[1])
              .lineTo(segment.control1[0], segment.control1[1])
              .moveTo(segment.end[0], segment.end[1])
              .lineTo(segment.control2[0], segment.control2[1])
              .stroke({ color: 0x8b9da7, width: 1, alpha: 0.85 });
            panel.addChild(controls);

            ([segment.control1, segment.control2] as const).forEach((position, controlIndex) => {
              const controlName = `control${controlIndex + 1}`;
              const id = `${edge.id}.segment[${segmentIndex}].${controlName}`;
              const selected = props.selectedId === id;
              const handle = new Graphics()
                .circle(position[0], position[1], selected ? 6 : 4.5)
                .fill(selected ? 0xff6b6b : 0x7dd3fc)
                .stroke({ color: selected ? 0xffffff : 0x16313d, width: 1.5 });
              handle.eventMode = "static";
              handle.cursor = "pointer";
              handle.on("pointerdown", (event) => { if (!tapeActive()) event.stopPropagation(); });
              handle.on("pointertap", (event) => {
                event.stopPropagation();
                if (!tapeActive()) props.onSelect({ id, kind: "control-handle", position: [...position] });
              });
              panel.addChild(handle);
            });
          });
        }
      }

      for (const mark of props.draft.marks.filter((item) => block.markIds.includes(item.id))) {
        const graphic = new Graphics();
        if (mark.operation === "distributeButtons") {
          for (const [x, y] of mark.points) {
            graphic.circle(x, y, 4).stroke({ color: 0x7dd3fc, width: 1.4 });
            graphic.moveTo(x - 3, y).lineTo(x + 3, y).moveTo(x, y - 3).lineTo(x, y + 3).stroke({ color: 0x7dd3fc, width: 1 });
          }
        } else if (mark.operation === "button") {
          const [x, y] = mark.points[0];
          graphic.circle(x, y, 4).stroke({ color: 0x7dd3fc, width: 1.4 });
          graphic.circle(x, y, 1.6).stroke({ color: 0x7dd3fc, width: 1.2 });
        } else if (mark.operation === "buttonhole") {
          // Keyhole: a round eye at the first point, the slit running on to a bar tack at the second.
          const [[ex, ey], [tx, ty]] = mark.points;
          const length = Math.hypot(tx - ex, ty - ey) || 1;
          const [ux, uy] = [(tx - ex) / length, (ty - ey) / length];
          const radius = 2;
          graphic.circle(ex, ey, radius).stroke({ color: 0x7dd3fc, width: 1.2 });
          graphic.moveTo(ex + ux * radius, ey + uy * radius).lineTo(tx, ty)
            .moveTo(tx - uy * radius, ty + ux * radius).lineTo(tx + uy * radius, ty - ux * radius)
            .stroke({ color: 0x7dd3fc, width: 1.2 });
        } else if (mark.operation === "notch") {
          const [x, y] = mark.points[0];
          graphic.moveTo(x - 6, y - 5).lineTo(x, y).lineTo(x - 6, y + 5).stroke({ color: 0xff8a65, width: 2 });
        } else {
          drawDashedLine(graphic, mark.points[0], mark.points[1], mark.operation === "foldMark" ? 12 : 7, 6);
          graphic.stroke({ color: mark.operation === "foldMark" ? 0x9ca3af : 0x7dd3fc, width: 1.2, alpha: 0.95 });
        }
        panel.addChild(graphic);
        if (mark.label && mark.points.length > 0) {
          const anchor = mark.points[Math.floor((mark.points.length - 1) / 2)];
          const label = new Text({ text: mark.label, style: { fill: mark.operation === "notch" ? 0xffab91 : 0xaac2ce, fontSize: 11, fontFamily: "monospace" } });
          label.position.set(anchor[0] + 8, anchor[1] - 16);
          panel.addChild(label);
        }
      }

      if (props.showPoints) {
        for (const pointId of block.pointIds) {
          const point = props.draft.points[pointId];
          if (!point) continue;
          const selected = props.selectedId === pointId;
          const graphic = new Graphics()
            .circle(point[0], point[1], selected ? 6 : 4)
            .fill(selected ? 0xff6b6b : 0x2dd4bf)
            .stroke({ color: selected ? 0xffffff : 0x123b38, width: 1.2 });
          const tooltip = pointTooltip(pointId, point, scale);
          pointTooltips.push(tooltip);
          graphic.eventMode = "static";
          graphic.cursor = "pointer";
          graphic.on("pointerover", () => { tooltip.visible = true; });
          graphic.on("pointerout", () => { tooltip.visible = false; });
          graphic.on("pointertap", (event) => {
            event.stopPropagation();
            if (!tapeActive()) props.onSelect({ id: pointId, kind: "point", position: [...point] });
          });
          panel.addChild(graphic);
          panel.addChild(tooltip);
        }
      }
      if (!printed) {
        // Dim a piece left out of the print, below the header; it stays selectable underneath.
        const veil = new Graphics().rect(frame.x, frame.y + FRAME_HEADER, frame.width, frame.height - FRAME_HEADER).fill({ color: 0x141d21, alpha: 0.7 });
        veil.eventMode = "none";
        panel.addChild(veil);
      }
      camera.addChild(panel);
    }
    tapeLayer = new Container();
    camera.addChild(tapeLayer);
    drawTape();
  };

  const drawTape = () => {
    if (!tapeLayer) return;
    tapeLayer.removeChildren().forEach((child) => child.destroy());
    if (!tapeStart || !tapeEnd) return;
    const reading = measureBetween(tapeStart, tapeEnd);
    const line = new Graphics();
    drawDashedLine(line, tapeStart, tapeEnd, 8 / scale, 5 / scale);
    line.stroke({ color: 0xff6b6b, width: 2 / scale, alpha: 1 });
    const endpointRadius = 5 / scale;
    line.circle(tapeStart[0], tapeStart[1], endpointRadius).fill(0xff6b6b).stroke({ color: 0xffffff, width: 1 / scale });
    line.circle(tapeEnd[0], tapeEnd[1], endpointRadius).fill(0xff6b6b).stroke({ color: 0xffffff, width: 1 / scale });
    tapeLayer.addChild(line);

    const label = new Text({
      text: `${reading.length.toFixed(1)} mm\nΔx ${Math.abs(reading.deltaX).toFixed(1)}  Δy ${Math.abs(reading.deltaY).toFixed(1)}`,
      style: { fill: 0xffffff, fontSize: 12 / scale, fontFamily: "monospace", align: "center", lineHeight: 16 / scale },
    });
    label.anchor.set(0.5, 1);
    label.position.set((tapeStart[0] + tapeEnd[0]) / 2, (tapeStart[1] + tapeEnd[1]) / 2 - 8 / scale);
    tapeLayer.addChild(label);
  };

  const pointerInWorld = (global: { x: number; y: number }): PointTuple => {
    if (!camera) return [global.x, global.y];
    const local = camera.toLocal(global);
    return snapToDraftPoint([local.x, local.y], props.draft, placements, 10 / scale);
  };

  const fitRect = (rect: Rectangle) => {
    if (!camera || !app || rect.width <= 0 || rect.height <= 0) return;
    const margin = 32;
    scale = Math.min((app.screen.width - margin * 2) / rect.width, (app.screen.height - margin * 2) / rect.height, 4);
    camera.scale.set(scale);
    camera.position.set((app.screen.width - rect.width * scale) / 2 - rect.x * scale, (app.screen.height - rect.height * scale) / 2 - rect.y * scale);
    resizePointTooltips();
    drawTape();
  };

  const fit = () => fitRect(new Rectangle(0, 0, layoutSize.width, layoutSize.height));

  const focusPiece = (blockId: string) => {
    const frame = frames.get(blockId);
    const placement = placements.get(blockId);
    if (frame && placement) fitRect(new Rectangle(frame.x + placement[0], frame.y + placement[1], frame.width, frame.height));
  };

  const resizePointTooltips = () => {
    for (const tooltip of pointTooltips) tooltip.scale.set(1 / scale);
  };

  // Pixi cannot destroy an Application before init() resolves, so a workspace
  // left while still starting up is torn down once init finishes.
  let disposed = false;
  let resizeObserver: ResizeObserver | undefined;

  onMount(async () => {
    const created = new Application();
    await created.init({ resizeTo: host, background: 0x101619, antialias: true, resolution: Math.min(devicePixelRatio, 2), autoDensity: true });
    if (disposed) {
      created.destroy(true, { children: true });
      return;
    }
    app = created;
    host.appendChild(app.canvas);
    camera = new Container();
    camera.eventMode = "static";
    app.stage.addChild(camera);
    app.stage.eventMode = "static";
    app.stage.hitArea = app.screen;
    app.stage.on("pointerdown", (event) => {
      touches.set(event.pointerId, { x: event.global.x, y: event.global.y });
      if (touches.size === 2) {
        // A second finger turns a pan or tape stroke into a pinch.
        dragging = false;
        measuring = false;
        pinch = pinchState();
        return;
      }
      if (touches.size > 2) return;
      if (tapeActive()) {
        measuring = true;
        tapeStart = pointerInWorld(event.global);
        tapeEnd = tapeStart;
        setTapeReading(measureBetween(tapeStart, tapeEnd));
        drawTape();
        return;
      }
      dragging = true;
      previous = { x: event.global.x, y: event.global.y };
    });
    const finishPointer = (event: { pointerId: number }) => {
      touches.delete(event.pointerId);
      pinch = undefined;
      dragging = false;
      measuring = false;
    };
    app.stage.on("pointerup", finishPointer);
    app.stage.on("pointerupoutside", finishPointer);
    app.stage.on("pointermove", (event) => {
      if (touches.has(event.pointerId)) touches.set(event.pointerId, { x: event.global.x, y: event.global.y });
      if (pinch && camera) {
        const next = pinchState();
        if (!next) return;
        zoomAt(next.midpoint, next.distance / pinch.distance);
        camera.position.x += next.midpoint.x - pinch.midpoint.x;
        camera.position.y += next.midpoint.y - pinch.midpoint.y;
        pinch = next;
        return;
      }
      if (tapeActive() && measuring && tapeStart) {
        tapeEnd = pointerInWorld(event.global);
        setTapeReading(measureBetween(tapeStart, tapeEnd));
        drawTape();
        return;
      }
      if (!dragging || !camera) return;
      camera.position.x += event.global.x - previous.x;
      camera.position.y += event.global.y - previous.y;
      previous = { x: event.global.x, y: event.global.y };
    });
    host.addEventListener("wheel", onWheel, { passive: false });
    host.addEventListener("pointercancel", cancelTouches);
    render();
    fit();
    // Re-pack when the canvas changes shape enough to suit a different arrangement.
    resizeObserver = new ResizeObserver(() => requestAnimationFrame(() => {
      if (disposed || !app || app.screen.height === 0) return;
      const aspect = app.screen.width / app.screen.height;
      if (Math.abs(Math.log(aspect / packedAspect)) < 0.15) return;
      render();
      fit();
    }));
    resizeObserver.observe(host);
  });

  /** Zoom by a factor while keeping the world point under `pointer` (screen pixels) still. */
  const zoomAt = (pointer: { x: number; y: number }, factor: number) => {
    if (!camera) return;
    const worldPoint = { x: (pointer.x - camera.x) / scale, y: (pointer.y - camera.y) / scale };
    scale = Math.min(4, Math.max(0.08, scale * factor));
    camera.scale.set(scale);
    camera.position.set(pointer.x - worldPoint.x * scale, pointer.y - worldPoint.y * scale);
    resizePointTooltips();
    drawTape();
  };

  const zoomCentre = (factor: number) => { if (app) zoomAt({ x: app.screen.width / 2, y: app.screen.height / 2 }, factor); };

  const pinchState = () => {
    const [a, b] = [...touches.values()];
    if (!a || !b) return undefined;
    return { distance: Math.max(Math.hypot(a.x - b.x, a.y - b.y), 1), midpoint: { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 } };
  };

  const cancelTouches = () => { touches.clear(); pinch = undefined; dragging = false; measuring = false; };

  const onWheel = (event: WheelEvent) => {
    event.preventDefault();
    const rect = host.getBoundingClientRect();
    zoomAt({ x: event.clientX - rect.left, y: event.clientY - rect.top }, Math.exp(-event.deltaY * 0.001));
  };

  const toggleTape = () => {
    setTapeActive((active) => !active);
    dragging = false;
    measuring = false;
    if (app) app.canvas.style.cursor = tapeActive() ? "crosshair" : "grab";
  };

  const clearTape = () => {
    tapeStart = undefined;
    tapeEnd = undefined;
    measuring = false;
    setTapeReading(undefined);
    drawTape();
  };

  createEffect(() => {
    props.draft;
    props.showGrid;
    props.showPoints;
    props.showCurveHandles;
    props.showMannequin;
    props.selectedId;
    props.printExcluded;
    render();
  });

  onCleanup(() => {
    disposed = true;
    resizeObserver?.disconnect();
    host?.removeEventListener("wheel", onWheel);
    host?.removeEventListener("pointercancel", cancelTouches);
    if (app) app.destroy(true, { children: true });
  });

  return (
    <section class="workspace-wrap" aria-label={`CAD workspace for ${props.draft.specificationId}`}>
      <div class="design-bar">{props.design}</div>
      <div class="canvas-tools">
        <div class="zoom-buttons" role="group" aria-label="Zoom">
          <button type="button" aria-label="Zoom out" onClick={() => zoomCentre(1 / 1.4)}>−</button>
          <button type="button" aria-label="Zoom in" onClick={() => zoomCentre(1.4)}>+</button>
          <button type="button" onClick={fit}>Fit all</button>
        </div>
        <select class="piece-focus" aria-label="Zoom to piece" onChange={(event) => { focusPiece(event.currentTarget.value); event.currentTarget.value = ""; }}>
          <option value="" selected>Zoom to piece…</option>
          <For each={props.draft.blocks}>{(block) => <option value={block.id}>{block.label}</option>}</For>
        </select>
        <button type="button" classList={{ active: tapeActive() }} aria-pressed={tapeActive()} onClick={toggleTape}>Measure</button>
        <Show when={tapeReading()}>{(reading) => <><strong class="tape-reading">{reading().length.toFixed(1)} mm</strong><button type="button" onClick={clearTape}>Clear</button></>}</Show>
        {props.tools}
        <span class="canvas-hint">{tapeActive() ? "Drag between two points · nearby pattern points snap" : coarsePointer ? "Pinch to zoom · drag to pan · double-tap a piece to focus" : "Wheel to zoom · drag to pan · double-click a piece to focus · 1 unit = 1 mm"}</span>
      </div>
      <div class="canvas-host" ref={host} />
    </section>
  );
}

/** Height of the title strip at the top of each piece's frame, in mm. */
const FRAME_HEADER = 40;
const PRINT_BUTTON = 24;

/** A printer drawn in a header-sized square, struck through when its piece is left out of the print. */
function printButton(printed: boolean): Container {
  const button = new Container();
  button.eventMode = "static";
  button.cursor = "pointer";
  button.hitArea = new Rectangle(-4, -4, PRINT_BUTTON + 8, PRINT_BUTTON + 8);
  const colour = printed ? 0xd7e3e8 : 0x6f8088;
  // The 24-unit PrintIcon from Icons.tsx.
  const icon = new Graphics()
    .moveTo(7, 8).lineTo(7, 3).lineTo(17, 3).lineTo(17, 8)
    .roundRect(3, 8, 18, 9, 2)
    .rect(7, 14, 10, 7)
    .stroke({ color: colour, width: 1.8 });
  if (!printed) icon.moveTo(3, 3).lineTo(21, 21).stroke({ color: 0xf28b82, width: 2.2 });
  button.addChild(icon);
  return button;
}
const FRAME_PADDING = 30;
const FRAME_GAP = 50;

interface PieceLayout {
  /** Each piece's frame, in its own drafting coordinates. */
  frames: Map<string, Rectangle>;
  /** Translation from drafting coordinates to the packed workspace. */
  placements: Map<string, PointTuple>;
  width: number;
  height: number;
}

/** Gives every piece its own frame and packs the frames to fill a canvas of the given shape. */
function layoutPieces(draft: DraftResult, aspect: number): PieceLayout {
  const frames = new Map<string, Rectangle>();
  for (const block of draft.blocks) {
    const content = contentBounds(block);
    frames.set(block.id, new Rectangle(
      content.minX - FRAME_PADDING,
      content.minY - FRAME_PADDING - FRAME_HEADER,
      Math.max(content.maxX - content.minX, 140) + FRAME_PADDING * 2,
      Math.max(content.maxY - content.minY, 100) + FRAME_PADDING * 2 + FRAME_HEADER,
    ));
  }
  const ordered = draft.blocks.map((block) => frames.get(block.id)!);
  const packed = packBoxes(ordered, aspect, FRAME_GAP);
  const placements = new Map<string, PointTuple>();
  draft.blocks.forEach((block, index) => {
    const frame = ordered[index];
    placements.set(block.id, [packed.positions[index].x - frame.x, packed.positions[index].y - frame.y]);
  });
  return { frames, placements, width: packed.width, height: packed.height };
}

/** The piece's bounds grown to include display-only overlays, so nothing spills out of its frame. */
function contentBounds(block: DraftBlock) {
  let { minX, minY, maxX, maxY } = block.bounds;
  const overlays = [...block.referenceOverlays, ...(block.bodyOverlay ? [block.bodyOverlay] : [])];
  for (const overlay of overlays) {
    for (const edge of overlay.edges) {
      for (const segment of edge.segments) {
        for (const [x, y] of sampleSegment(segment, segment.kind === "bezier" ? 16 : 1)) {
          minX = Math.min(minX, x); minY = Math.min(minY, y);
          maxX = Math.max(maxX, x); maxY = Math.max(maxY, y);
        }
      }
    }
  }
  return { minX, minY, maxX, maxY };
}

/** A 10 mm grid, major every 100 mm, aligned to the drafting origin and kept inside the frame body. */
function frameGrid(frame: Rectangle): Graphics {
  const grid = new Graphics();
  const top = frame.y + FRAME_HEADER;
  const bottom = frame.y + frame.height;
  const right = frame.x + frame.width;
  const line = (major: boolean) => ({ color: major ? 0x34434a : 0x212c31, width: major ? 0.8 : 0.35 });
  for (let x = Math.ceil(frame.x / 10) * 10; x <= right; x += 10) grid.moveTo(x, top).lineTo(x, bottom).stroke(line(x % 100 === 0));
  for (let y = Math.ceil(top / 10) * 10; y <= bottom; y += 10) grid.moveTo(frame.x, y).lineTo(right, y).stroke(line(y % 100 === 0));
  return grid;
}

function pointTooltip(pointId: string, point: PointTuple, scale: number): Container {
  const tooltip = new Container();
  tooltip.position.set(point[0], point[1]);
  tooltip.scale.set(1 / scale);
  tooltip.visible = false;
  tooltip.eventMode = "none";

  const label = new Text({
    text: pointId,
    style: { fill: 0xf3faf9, fontSize: 12, fontFamily: "monospace", fontWeight: "bold" },
  });
  label.position.set(10, -26);
  const background = new Graphics()
    .roundRect(5, -31, label.width + 10, label.height + 8, 4)
    .fill({ color: 0x0c171a, alpha: 0.94 })
    .stroke({ color: 0x2dd4bf, width: 1 });
  tooltip.addChild(background, label);
  return tooltip;
}

function snapToDraftPoint(point: PointTuple, draft: DraftResult, placements: Map<string, PointTuple>, radius: number): PointTuple {
  let nearest = point;
  let nearestDistance = radius;
  for (const block of draft.blocks) {
    const offset = placements.get(block.id) ?? block.offset;
    for (const pointId of block.pointIds) {
      const candidate = draft.points[pointId];
      if (!candidate) continue;
      const positioned: PointTuple = [candidate[0] + offset[0], candidate[1] + offset[1]];
      const distance = Math.hypot(positioned[0] - point[0], positioned[1] - point[1]);
      if (distance <= nearestDistance) {
        nearest = positioned;
        nearestDistance = distance;
      }
    }
  }
  return nearest;
}

function formatDimension(value: number): string {
  return `${Math.round(value)} mm`;
}

function drawGrainArrow(graphic: Graphics, from: [number, number], to: [number, number]) {
  graphic.moveTo(from[0], from[1]).lineTo(to[0], to[1]);
  const angle = Math.atan2(to[1] - from[1], to[0] - from[0]);
  for (const [point, direction] of [[from, angle], [to, angle + Math.PI]] as const) {
    graphic
      .moveTo(point[0], point[1])
      .lineTo(point[0] + Math.cos(direction - 0.55) * 8, point[1] + Math.sin(direction - 0.55) * 8)
      .moveTo(point[0], point[1])
      .lineTo(point[0] + Math.cos(direction + 0.55) * 8, point[1] + Math.sin(direction + 0.55) * 8);
  }
}

function drawDashedLine(graphic: Graphics, from: [number, number], to: [number, number], dash: number, gap: number): void {
  const length = Math.hypot(to[0] - from[0], to[1] - from[1]);
  if (length === 0) return;
  const dx = (to[0] - from[0]) / length;
  const dy = (to[1] - from[1]) / length;
  for (let distance = 0; distance < length; distance += dash + gap) {
    const end = Math.min(distance + dash, length);
    graphic.moveTo(from[0] + dx * distance, from[1] + dy * distance).lineTo(from[0] + dx * end, from[1] + dy * end);
  }
}

/** Diagonal hatch lines across a polygon; direction -1 runs them the other way. */
function drawCrossHatch(graphic: Graphics, polygon: [number, number][], spacing: number, direction: 1 | -1 = 1): void {
  if (polygon.length < 3) return;
  const sums = polygon.map(([x, y]) => x + direction * y);
  for (let diagonal = Math.floor(Math.min(...sums) / spacing) * spacing; diagonal <= Math.max(...sums); diagonal += spacing) {
    const intersections: [number, number][] = [];
    for (let index = 0; index < polygon.length; index += 1) {
      const from = polygon[index];
      const to = polygon[(index + 1) % polygon.length];
      const fromValue = from[0] + direction * from[1] - diagonal;
      const toValue = to[0] + direction * to[1] - diagonal;
      if ((fromValue < 0 && toValue >= 0) || (toValue < 0 && fromValue >= 0)) {
        const ratio = fromValue / (fromValue - toValue);
        intersections.push([from[0] + (to[0] - from[0]) * ratio, from[1] + (to[1] - from[1]) * ratio]);
      }
    }
    intersections.sort((a, b) => a[0] - b[0]);
    for (let index = 0; index + 1 < intersections.length; index += 2) {
      graphic.moveTo(intersections[index][0], intersections[index][1]).lineTo(intersections[index + 1][0], intersections[index + 1][1]);
    }
  }
}

function distanceToSegment(x: number, y: number, from: [number, number], to: [number, number]): number {
  const dx = to[0] - from[0];
  const dy = to[1] - from[1];
  const lengthSquared = dx * dx + dy * dy;
  if (lengthSquared === 0) return Math.hypot(x - from[0], y - from[1]);
  const t = Math.max(0, Math.min(1, ((x - from[0]) * dx + (y - from[1]) * dy) / lengthSquared));
  return Math.hypot(x - (from[0] + t * dx), y - (from[1] + t * dy));
}
